import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
} from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { ErrorLink } from '@apollo/client/link/error';

import generatedIntrospection from '@/graphql/generated/fragment';
import { isIpad } from '@/utils/user_agent';

type HeadersInit = Headers | string[][] | Record<string, string>;

/**
 * @see https://www.apollographql.com/docs/react/api/link/apollo-link-batch-http/#dynamic-uri
 */
const fetchWithLiffIdAndUserId = (
  uri: RequestInfo | URL,
  options?: RequestInit,
  liffId?: string,
  userId?: string
): Promise<Response> => {
  const newOptions = { ...options };
  // bodyが存在し、JSONとしてパースできる場合はliffIdとuserIdをbodyに追加
  if (options && options.body) {
    try {
      const bodyObj: unknown = JSON.parse(options.body as string);
      // liffIdとuserIdをbodyに追加
      if (Array.isArray(bodyObj)) {
        bodyObj.forEach(obj => {
          if (typeof obj === 'object' && obj !== null) {
            (obj as Record<string, unknown>)['liffId'] = liffId;
            if (userId) {
              (obj as Record<string, unknown>)['userId'] = userId;
            }
          }
        });
      } else if (typeof bodyObj === 'object' && bodyObj !== null) {
        (bodyObj as Record<string, unknown>)['liffId'] = liffId;
        if (userId) {
          (bodyObj as Record<string, unknown>)['userId'] = userId;
        }
      }
      newOptions.body = JSON.stringify(bodyObj);
    } catch {
      // パースできなければ何もしない
    }
  }

  return fetch(uri, newOptions);
};

export const createApolloClient = (liffId: string, userId?: string) => {
  const httpLink = new HttpLink({
    uri: `${process.env.apiBaseUrl}/line/graphql`,
    fetch: (uri, options) =>
      fetchWithLiffIdAndUserId(uri, options, liffId, userId),
  });

  const authLink = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }: { headers: HeadersInit }) => {
      headers = {
        ...headers,
        'X-Pharms-Service': 'kain',
        'X-Datadog-Env': 'kain-request',
      };

      if (isIpad()) {
        // kinesissToBigQueryでdevice判定してる処理を将来的にフロントに移す予定
        // iPadOSだけはdevice判定できないため、先に移した
        headers = {
          ...headers,
          'X-Device-Type': 'tablet',
        };
      }

      const contextHeader = {
        headers: {
          ...headers,
        },
      };
      return contextHeader;
    });

    return forward(operation);
  });

  const errorLink = new ErrorLink(({ error }) => {
    if (CombinedGraphQLErrors.is(error)) {
      error.errors.forEach(({ message, locations, path }) => {
        console.error(
          `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${JSON.stringify(path)}`
        );
      });
    } else {
      console.error(
        `[Network error]: ${error instanceof Error ? error.message : JSON.stringify(error)}`
      );
    }
  });

  const cache = new InMemoryCache({
    possibleTypes: generatedIntrospection.possibleTypes,
  });

  return new ApolloClient({
    ssrMode: false,
    link: ApolloLink.from([errorLink, authLink, httpLink]),
    cache,
    defaultOptions: {
      query: {
        errorPolicy: 'all',
      },
    },
    devtools: {
      enabled: true,
    },
  });
};
